import axios from 'axios';
import URLSearchParams from '@ungap/url-search-params';

// Получение store через функцию с динамическим импортом используется
// для разрешения циклических зависимостей между модулями.
const getStore = () => import('@src/store').then(module => module.default);

const axiosInstance = axios.create({
  withCredentials: true
});
const cache = {};

export const setApiAdapterOptions = (baseUrl, timeout) => {
  axiosInstance.defaults.baseURL = baseUrl;
  axiosInstance.defaults.timeout = timeout;
};

export default {
  /**
   * @param {string} url
   * @param {Object} [options]
   * @param {CancelTokenSource} [cancelSource]
   * @param {boolean} [restoreSession=true]
   * @returns {Promise<*, AxiosError>}
   */
  get(url, options = null, cancelSource = null, restoreSession = true) {
    return axiosInstance
      .get(url, this.makeRequestConfig(options, cancelSource))
      .catch(error => this.handleError(error, restoreSession));
  },

  /**
   * @param {string} url
   * @param {Object} [options]
   * @param {CancelTokenSource} [cancelSource]
   * @returns {Promise<*, AxiosError>}
   */
  getData(url, options = null, cancelSource = null) {
    return this.get(url, options, cancelSource).then(({ data }) => data);
  },

  /**
   * @param {string} url
   * @param {Array} ids
   * @returns {Promise<*, AxiosError>}
   */
  getDataByIds(url, ids) {
    return this.getData(url, {
      params: { ids: ids.join(',') }
    });
  },

  /**
   * @param {string} url
   * @param {string|Object} [data]
   * @param {boolean} [restoreSession=true]
   * @returns {Promise<*, AxiosError>}
   */
  post(url, data = null, restoreSession = true) {
    return axiosInstance
      .post(url, this.makePostParams(data))
      .catch(error => this.handleError(error, restoreSession));
  },

  /**
   * @param {Object|null} options
   * @param {CancelTokenSource} [cancelSource]
   * @returns {Object}
   */
  makeRequestConfig(options, cancelSource = null) {
    let config = options ? { ...options } : {};

    if (cancelSource) {
      config.cancelToken = cancelSource.token;
    }
    return config;
  },

  /**
   * @param {*|Object} params
   * @returns {*|string}
   */
  makePostParams(params) {
    if (params === null || typeof params !== 'object') {
      return params;
    }
    return new URLSearchParams(params).toString();
  },

  /**
   * Обрабатывает ошибку, полученную в процессе выполнения запроса.
   *
   * @param {AxiosError} error
   * @param {boolean} restoreSession
   * @returns {Promise<*, AxiosError>}
   */
  handleError(error, restoreSession) {
    // Если ошибка возникла из-за отсутствия активной сессии пользователя,
    // то пробуем восстановить сессию и заново отправить запрос.
    if (this.isErrorUnauthorized(error) && restoreSession) {
      return getStore().then(store =>
        store.dispatch('authCheck').then(success => {
          if (success) {
            // Сессия пользователя успешно восстановлена,
            // заново отправляем запрос.
            return axiosInstance
              .request(error.config)
              .catch(error => Promise.reject(this.logError(error)));
          }
          return Promise.reject(this.logError(error));
        })
      );
    }
    return Promise.reject(this.logError(error));
  },

  /**
   * @param {AxiosError} error
   * @returns {AxiosError}
   */
  logError(error) {
    if (this.isErrorCancel(error)) {
      console.log(error);
    } else {
      console.error(error);
    }
    return error;
  },

  /**
   * @returns {CancelTokenSource}
   */
  getCancelTokenSource() {
    return axios.CancelToken.source();
  },

  /**
   * @param {AxiosError} error
   * @returns {boolean}
   */
  isErrorCancel(error) {
    return axios.isCancel(error);
  },

  /**
   * @param {AxiosError} error
   * @returns {boolean}
   */
  isErrorUnauthorized(error) {
    return this.checkErrorStatus(error, 401);
  },

  /**
   * @param {AxiosError} error
   * @returns {boolean}
   */
  isErrorForbidden(error) {
    return this.checkErrorStatus(error, 403);
  },

  /**
   * @param {AxiosError} error
   * @returns {boolean}
   */
  isErrorNotFound(error) {
    return this.checkErrorStatus(error, 404);
  },

  /**
   * @param {AxiosError} error
   * @param {number} statusCode
   * @returns {boolean}
   */
  checkErrorStatus(error, statusCode) {
    return error.response && error.response.status === statusCode;
  },

  /**
   * @param {string} key
   * @returns {*|undefined}
   */
  getCache(key) {
    return cache[key];
  },

  /**
   * @param {string} key
   * @param {*} value
   */
  setCache(key, value) {
    cache[key] = value;
  }
};
